import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { ConfirmationDialog } from '../../components/ui/confirmation-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Plus,
  Pencil,
  Trash2,
  Rocket,
  Clock,
  CheckCircle2,
  Zap,
  Brain,
  BarChart3,
  FileText,
  Smartphone,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  Shield,
  Calendar,
  TrendingUp,
  Mic,
  DollarSign,
  Truck,
  Users,
  Lightbulb,
} from 'lucide-react';
import { useSuperAdmin } from '../../context/SuperAdminContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/super-admin';

// Icon mapping
const ICON_OPTIONS = [
  { value: 'FileText', label: 'Document', icon: FileText },
  { value: 'Shield', label: 'Shield', icon: Shield },
  { value: 'Calendar', label: 'Calendar', icon: Calendar },
  { value: 'Brain', label: 'Brain (AI)', icon: Brain },
  { value: 'TrendingUp', label: 'Trending', icon: TrendingUp },
  { value: 'Mic', label: 'Microphone', icon: Mic },
  { value: 'DollarSign', label: 'Dollar', icon: DollarSign },
  { value: 'Truck', label: 'Truck', icon: Truck },
  { value: 'Users', label: 'Users', icon: Users },
  { value: 'BarChart3', label: 'Chart', icon: BarChart3 },
  { value: 'Smartphone', label: 'Mobile', icon: Smartphone },
  { value: 'Lightbulb', label: 'Idea', icon: Lightbulb },
  { value: 'Rocket', label: 'Rocket', icon: Rocket },
  { value: 'Zap', label: 'Lightning', icon: Zap },
];

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned', color: 'bg-amber-100 text-amber-800' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
  { value: 'released', label: 'Released', color: 'bg-green-100 text-green-800' },
];

const PHASE_OPTIONS = [
  { value: 'short', label: 'Short Term (Q1-Q2)' },
  { value: 'medium', label: 'Medium Term (Q3-Q4)' },
  { value: 'long', label: 'Long Term (2026+)' },
  { value: 'released', label: 'Released' },
];

const CATEGORY_OPTIONS = [
  { value: 'core', label: 'Core Feature' },
  { value: 'ai', label: 'AI-Powered' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'mobile', label: 'Mobile' },
];

const RoadmapManagement = () => {
  const { isDark = true } = useOutletContext() || {};
  const { api } = useSuperAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    status: 'planned',
    phase: 'medium',
    category: 'core',
    icon: 'FileText',
    value: '',
    eta: '',
    order: 0
  });

  const fetchItems = useCallback(async () => {
    try {
      const response = await api.get(`/api/super-admin/roadmap`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch roadmap items');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      status: 'planned',
      phase: 'medium',
      category: 'core',
      icon: 'FileText',
      value: '',
      eta: '',
      order: items.length
    });
    setEditingItem(null);
  };

  const handleOpenSheet = (item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        title: item.title,
        description: item.description,
        status: item.status,
        phase: item.phase,
        category: item.category,
        icon: item.icon,
        value: item.value || '',
        eta: item.eta || '',
        order: item.order || 0
      });
    } else {
      resetForm();
    }
    setSheetOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/api/super-admin/roadmap/${editingItem.id}`, form);
        toast.success('Roadmap item updated');
      } else {
        await api.post(`/api/super-admin/roadmap`, form);
        toast.success('Roadmap item created');
      }
      setSheetOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Roadmap Item',
      description: 'Are you sure you want to delete this roadmap item?',
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        try {
          await api.delete(`/api/super-admin/roadmap/${id}`);
          toast.success('Roadmap item deleted');
          fetchItems();
        } catch (error) {
          toast.error('Failed to delete item');
        }
      },
    });
  };

  const handleMoveOrder = async (item, direction) => {
    const currentIndex = items.findIndex(i => i.id === item.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= items.length) return;
    
    const reorderedItems = [...items];
    const [removed] = reorderedItems.splice(currentIndex, 1);
    reorderedItems.splice(newIndex, 0, removed);
    
    const updatedOrder = reorderedItems.map((item, index) => ({
      id: item.id,
      order: index
    }));
    
    try {
      await api.put(`/api/super-admin/roadmap/reorder`, updatedOrder);
      fetchItems();
    } catch (error) {
      toast.error('Failed to reorder items');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status);
    return (
      <Badge className={statusConfig?.color || 'bg-gray-100'}>
        {status === 'released' && <CheckCircle2 className="h-3 w-3 mr-1" />}
        {status === 'in_progress' && <Zap className="h-3 w-3 mr-1" />}
        {status === 'planned' && <Clock className="h-3 w-3 mr-1" />}
        {statusConfig?.label || status}
      </Badge>
    );
  };

  const getCategoryBadge = (category) => {
    const colors = {
      ai: 'border-purple-300 text-purple-700',
      analytics: 'border-blue-300 text-blue-700',
      core: 'border-gray-300 text-gray-700',
      mobile: 'border-green-300 text-green-700'
    };
    return (
      <Badge variant="outline" className={colors[category] || ''}>
        {category === 'ai' && <Brain className="h-3 w-3 mr-1" />}
        {category === 'analytics' && <BarChart3 className="h-3 w-3 mr-1" />}
        {category === 'mobile' && <Smartphone className="h-3 w-3 mr-1" />}
        {category === 'core' && <Rocket className="h-3 w-3 mr-1" />}
        {CATEGORY_OPTIONS.find(c => c.value === category)?.label || category}
      </Badge>
    );
  };

  const IconComponent = ({ iconName }) => {
    const iconConfig = ICON_OPTIONS.find(i => i.value === iconName);
    const Icon = iconConfig?.icon || FileText;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  // Group items by status for summary
  const releasedCount = items.filter(i => i.status === 'released').length;
  const inProgressCount = items.filter(i => i.status === 'in_progress').length;
  const plannedCount = items.filter(i => i.status === 'planned').length;

  return (
    <>
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Rocket className="h-6 w-6 text-[#ffd108]" />
            Product Roadmap Management
          </h1>
          <p className="text-muted-foreground">Manage roadmap items visible to all tenants</p>
        </div>
        <Button className="btn-volt" onClick={() => handleOpenSheet()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{releasedCount}</p>
                <p className="text-xs text-muted-foreground">Released</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Zap className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inProgressCount}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{plannedCount}</p>
                <p className="text-xs text-muted-foreground">Planned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Brain className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{items.filter(i => i.category === 'ai').length}</p>
                <p className="text-xs text-muted-foreground">AI Features</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Items Table */}
      <Card>
        <CardHeader>
          <CardTitle>Roadmap Items ({items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-12">
              <Rocket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No roadmap items yet</p>
              <Button className="mt-4 btn-volt" onClick={() => handleOpenSheet()}>
                Add Your First Item
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Order</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Phase</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>ETA</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => handleMoveOrder(item, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => handleMoveOrder(item, 'down')}
                          disabled={index === items.length - 1}
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <IconComponent iconName={item.icon} />
                        <span className="font-medium">{item.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(item.status)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {PHASE_OPTIONS.find(p => p.value === item.phase)?.label || item.phase}
                      </span>
                    </TableCell>
                    <TableCell>{getCategoryBadge(item.category)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{item.eta || '-'}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenSheet(item)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDelete(item.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl">
              {editingItem ? 'Edit Roadmap Item' : 'Add Roadmap Item'}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title <span className="text-red-500">*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Feature title"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Description <span className="text-red-500">*</span></Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Detailed description of the feature..."
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Phase</Label>
                <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PHASE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className="h-4 w-4" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Value Proposition</Label>
              <Input
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder="e.g., Reduce invoice turnaround by 50%"
              />
              <p className="text-xs text-muted-foreground">The key benefit highlighted to users</p>
            </div>

            <div className="space-y-2">
              <Label>ETA</Label>
              <Input
                value={form.eta}
                onChange={(e) => setForm({ ...form, eta: e.target.value })}
                placeholder="e.g., Q2 2025, Released"
              />
            </div>

            <SheetFooter className="pt-4 border-t mt-6">
              <Button type="button" variant="outline" onClick={() => setSheetOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt">
                {editingItem ? 'Update' : 'Create'} Item
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        confirmText="Delete"
        variant="destructive"
      />
    </>
  );
};

export default RoadmapManagement;
